import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  UserPlus,
  Upload,
  Sparkles,
  Wand2,
  Download,
  Share2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { seoHead } from "@/lib/seo";

export const Route = createFileRoute("/how-to-use")({
  head: () =>
    seoHead({
      title: "使い方ガイド - KAGEM",
      description:
        "KAGEMの使い方をステップごとに解説。アカウント登録から素材アップロード、AI動画生成、SNS共有まで最短3分。",
      path: "/how-to-use",
    }),
  component: HowToUsePage,
});

const steps = [
  {
    icon: UserPlus,
    title: "1. アカウント登録",
    desc: "メールアドレスまたはGoogleアカウントで30秒で登録完了。新規登録で無料クレジットをプレゼント。",
  },
  {
    icon: Upload,
    title: "2. 素材をアップロード",
    desc: "顔写真や元動画をアップロード。スマホで撮ったままの写真でOK。1枚でも複数枚でも対応します。",
  },
  {
    icon: Wand2,
    title: "3. モードを選択",
    desc: "「変身動画」「画像→動画」「ストーリーリール」「リップシンク」など、目的に合ったモードを選びます。",
  },
  {
    icon: Sparkles,
    title: "4. プロンプトと品質を設定",
    desc: "イメージを日本語で入力。品質(720p/1080p)や秒数を選んで「生成開始」をタップ。",
  },
  {
    icon: Download,
    title: "5. 完成・ダウンロード",
    desc: "通常1〜5分で完成。履歴ページからいつでもMP4でダウンロードできます。",
  },
  {
    icon: Share2,
    title: "6. SNSへ投稿",
    desc: "そのままTikTok・Instagramリール・YouTube Shortsに最適化された縦型動画として共有できます。",
  },
];

const tips = [
  {
    title: "顔写真は正面・明るい場所で",
    desc: "顔がはっきり映った写真ほど精度が上がります。サングラスやマスクは避けてください。",
  },
  {
    title: "プロンプトは具体的に",
    desc: "「夕日の海辺で笑顔で踊る」のように、シーン・表情・動きを具体的に書くとイメージ通りに仕上がります。",
  },
  {
    title: "まずはLightプランで",
    desc: "月300クレジットのLightプランから始めて、必要に応じて上位プランへ進むのがおすすめです。",
  },
];

function HowToUsePage() {
  return (
    <main className="min-h-screen bg-background">
      <div className="mx-auto max-w-5xl px-4 py-12 sm:py-20">
        <div className="mb-8">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/">
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              ホームへ戻る
            </Link>
          </Button>
        </div>

        <header className="mb-12 text-center sm:mb-16">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">使い方ガイド</h1>
          <p className="mt-4 text-muted-foreground">
            登録から動画完成まで最短3分。誰でも簡単にAI変身動画が作れます。
          </p>
        </header>

        <section className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {steps.map((s) => {
            const Icon = s.icon;
            return (
              <Card
                key={s.title}
                className="flex h-full flex-col p-6 transition-all hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="bg-gradient-brand mb-4 flex h-12 w-12 items-center justify-center rounded-xl text-white">
                  <Icon className="h-6 w-6" />
                </div>
                <h2 className="text-lg font-bold">{s.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
              </Card>
            );
          })}
        </section>

        <section className="mt-20">
          <h2 className="mb-8 text-center text-2xl font-bold sm:text-3xl">
            きれいに作るコツ
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {tips.map((t) => (
              <Card key={t.title} className="border-border/60 p-5">
                <h3 className="text-sm font-semibold">{t.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t.desc}</p>
              </Card>
            ))}
          </div>
        </section>

        <section className="mt-20 text-center">
          <Card className="bg-gradient-brand mx-auto max-w-2xl border-0 p-10 text-white">
            <h2 className="text-2xl font-bold sm:text-3xl">さっそく作ってみましょう</h2>
            <p className="mx-auto mt-3 max-w-md text-sm text-white/85">
              無料登録で今すぐ始められます。クレジットカード不要。
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button asChild size="lg" variant="secondary">
                <Link to="/signup">無料で始める</Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"
              >
                <Link to="/pricing">料金プランを見る</Link>
              </Button>
            </div>
          </Card>
        </section>
      </div>
    </main>
  );
}
